import React, { useCallback, useEffect, useRef, useState } from 'react'
import { ActionButtons, Badge } from '@enamelai/design-system'
import { Icon as DeleteIcon } from '@enamelai/design-system/icons/Delete2'
import { Icon as DownloadIcon } from '@enamelai/design-system/icons/Download'
import { ICalls } from '@dataTypes/calls.type'
import { useCallRecordingObjectUrl } from '@hooks/useCallRecording'

export const CallRecordingTypeCell = (props: ICalls): React.ReactElement => {
  const inbound = ['Incoming', 'Missed'].includes(props.call_type)
  return (
    <Badge
      mode="tinted"
      color={inbound ? 'blue' : 'indigo'}
      label={inbound ? 'Inbound' : 'Outbound'}
    />
  )
}

type ActionsProps = {
  call: ICalls
  onDownload: (c: ICalls) => void
  onRequestDelete: (c: ICalls) => void
}

export const CallRecordingActionsCell = (
  props: ActionsProps,
): React.ReactElement => (
  <ActionButtons
    actions={[
      {
        id: 'download',
        icon: (
          <DownloadIcon className="ds-action-button__svg" aria-hidden="true" />
        ),
        label: 'Download',
        tone: 'primary',
        variant: 'outline',
        onClick: () => props.onDownload(props.call),
      },
      {
        id: 'delete',
        icon: (
          <DeleteIcon className="ds-action-button__svg" aria-hidden="true" />
        ),
        label: 'Delete',
        tone: 'danger',
        variant: 'outline',
        onClick: () => props.onRequestDelete(props.call),
      },
    ]}
  />
)

type AudioProps = {
  call: ICalls
}

export const CallRecordingAudioCell = (
  props: AudioProps,
): React.ReactElement => {
  const { call } = props
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const playbackSrc = useCallRecordingObjectUrl(call.recording_url)
  const [playing, setPlaying] = useState(false)
  const [cur, setCur] = useState(0)
  const [dur, setDur] = useState(0)

  const fmt = (s: number): string => {
    if (!Number.isFinite(s) || s < 0) return '0:00'
    const m = Math.floor(s / 60)
    const sec = Math.floor(s % 60)
    return `${m}:${sec.toString().padStart(2, '0')}`
  }

  const toggle = useCallback((): void => {
    const el = audioRef.current
    if (!el || !playbackSrc) return
    if (playing) {
      el.pause()
      setPlaying(false)
    } else {
      void el
        .play()
        .then(() => setPlaying(true))
        .catch(() => setPlaying(false))
    }
  }, [playbackSrc, playing])

  useEffect(() => {
    const el = audioRef.current
    if (!el) return
    const onTime = (): void => {
      setCur(el.currentTime)
      setDur(el.duration || 0)
    }
    const onEnd = (): void => {
      setPlaying(false)
      setCur(0)
    }
    el.addEventListener('timeupdate', onTime)
    el.addEventListener('ended', onEnd)
    return () => {
      el.removeEventListener('timeupdate', onTime)
      el.removeEventListener('ended', onEnd)
    }
  }, [playbackSrc])

  return (
    <div className="scr-audio-player">
      {playbackSrc ? (
        <audio ref={audioRef} src={playbackSrc} preload="none" />
      ) : null}
      <button
        type="button"
        className="scr-play-btn"
        onClick={toggle}
        disabled={!playbackSrc}
      >
        <span
          className={`scr-play-btn-icon ${playing ? 'mgc_pause_circle_fill' : 'mgc_play_circle_fill'}`}
          aria-hidden
        />
      </button>
      <button
        type="button"
        className="scr-play-btn-text"
        onClick={toggle}
        disabled={!playbackSrc}
      >
        Play Audio
      </button>
      <span className="scr-audio-duration">
        {fmt(cur)}{' '}
        <span className="scr-total-duration">
          /{fmt(dur || call.duration || 0)}
        </span>
      </span>
    </div>
  )
}
